import { useId } from "react";
import { KeyCap, KeyChord } from "@/presentation/components/KeyCap";
import { Modal } from "@/presentation/components/overlay/Modal";
import "./KeyListModal.css";

interface KeyListModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

interface ShortcutEntry {
  readonly keys: readonly string[];
  readonly description: string;
}

interface ShortcutGroup {
  readonly heading: string;
  readonly entries: readonly ShortcutEntry[];
}

/**
 * 本サイトの「アプリの操作」であって、稽古の中で教える vim のキー自体は載せない
 * （vim キーは各段で文脈込みに教えるため、ここで一覧化すると二重の情報源になる）。
 * 「どのキーが何をするか」ではなく「その記号をどう打つか」（日本語配列での打ち方）は
 * 別軸の情報なので、下の `JIS_INPUT_EXAMPLES` として併記する。
 */
const SHORTCUT_GROUPS: readonly ShortcutGroup[] = [
  {
    heading: "門（はじめの画面）",
    entries: [
      { keys: ["↑", "↓"], description: "品書きの項目を移動する" },
      { keys: ["Enter"], description: "選んだ項目を開く" },
    ],
  },
  {
    heading: "復習の刻",
    entries: [
      { keys: ["Enter"], description: "合格後、次の想起課題へ進む" },
      { keys: ["Esc"], description: "門へ戻る（ヘッダの戻ると同じ）" },
    ],
  },
  {
    heading: "稽古の段（段の一覧）",
    entries: [
      { keys: ["↑", "↓"], description: "段の行を移動する" },
      { keys: ["Enter"], description: "選んだ段を開く" },
      { keys: ["m"], description: "免状帳を開く" },
      { keys: ["Esc"], description: "門へ戻る" },
    ],
  },
  {
    heading: "免状帳",
    entries: [
      { keys: ["←", "→", "↑", "↓"], description: "枠を移動する" },
      { keys: ["Enter"], description: "選んだ段へ進む" },
      { keys: ["Esc"], description: "稽古の段へ戻る" },
    ],
  },
  {
    heading: "稽古（墨板）",
    entries: [
      { keys: ["Tab"], description: "墨板から離れる（打鍵の的から出る）" },
      { keys: ["Enter"], description: "合格後、どこにいても次の問いへ進む" },
      { keys: ["Shift", "Esc"], description: "稽古を終了する（中断確認を開く）" },
      { keys: ["Esc"], description: "墨板の外にいるとき、稽古を終了する" },
    ],
  },
  {
    heading: "共通",
    entries: [
      { keys: ["?"], description: "このキー一覧を開く" },
      { keys: ["Esc"], description: "開いている帳・確認を閉じる" },
    ],
  },
];

interface JisExample {
  readonly notation: string;
  readonly description: string;
}

/**
 * 日本語（JIS）配列での打ち方。US配列の解説を読むと取り違えやすい記号だけを挙げる。
 * どの技に使うキーかは各段で教えるため、ここでは「打ち方」だけを示す。
 */
const JIS_INPUT_EXAMPLES: readonly JisExample[] = [
  { notation: "$", description: "Shift と 4 を同時に押す" },
  { notation: "*", description: "Shift と : を同時に押す（US配列の 8 ではない）" },
  { notation: "?", description: "Shift と / を同時に押す" },
  { notation: '"', description: "Shift と 2 を同時に押す（US配列の ' ではない）" },
  { notation: "`", description: "Shift と @ を同時に押す" },
  { notation: "^", description: "そのまま押す（日本語配列では Shift は要らない）" },
  { notation: ":", description: "そのまま押す（日本語配列では Shift は要らない）" },
  { notation: "<C-r>", description: "Ctrl を押しながら R を押す" },
];

/**
 * キー一覧モーダル（O-5。screen-flow.md §2.3）。参照専用（操作を受け付けない）。
 * 全体で共有される`?`キーでの起動は統合側（画面のキー入力ハンドラ）が担う——
 * このコンポーネント自体は`keydown`を監視せず、`open`/`onClose`のpropsのみで動く。
 */
export function KeyListModal({ open, onClose }: KeyListModalProps) {
  const titleId = useId();

  return (
    <Modal open={open} onClose={onClose} titleId={titleId}>
      <h2 id={titleId} className="key-list-modal__title">
        キー一覧
      </h2>
      <div className="key-list-modal__groups">
        {SHORTCUT_GROUPS.map((group) => (
          <section key={group.heading} className="key-list-modal__group">
            <h3 className="key-list-modal__group-heading">{group.heading}</h3>
            <ul className="key-list-modal__list">
              {group.entries.map((entry) => (
                <li key={entry.description} className="key-list-modal__entry">
                  <span className="key-list-modal__keys">
                    {entry.keys.map((key) => (
                      <KeyCap key={key}>{key}</KeyCap>
                    ))}
                  </span>
                  <span className="key-list-modal__desc">{entry.description}</span>
                </li>
              ))}
            </ul>
          </section>
        ))}
        <section className="key-list-modal__group">
          <h3 className="key-list-modal__group-heading">日本語キーボードでの打ち方</h3>
          <ul className="key-list-modal__list">
            {JIS_INPUT_EXAMPLES.map((example) => (
              <li key={example.notation} className="key-list-modal__entry">
                <span className="key-list-modal__keys">
                  <KeyChord notation={example.notation} />
                </span>
                <span className="key-list-modal__desc">
                  <code className="key-list-modal__notation">{example.notation}</code>
                  {` — ${example.description}`}
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </Modal>
  );
}
